import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  ClipboardList,
  Users2,
  Package,
  Wallet,
  Receipt,
  AlertTriangle,
  Building2,
  TrendingDown,
  Scale,
} from "lucide-react";
import {
  Line,
  LineChart,
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

import { PageHeader } from "@/components/PageHeader";
import { AlertsWidget } from "@/components/alerts/AlertsWidget";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

import { DateRangeFilter, presetRange } from "@/components/reports/DateRangeFilter";
import { BranchFilter } from "@/components/reports/BranchFilter";
import { KpiCard, formatMxn, formatInt } from "@/components/reports/KpiCard";
import { ReorderAlert } from "@/components/reports/ReorderAlert";
import { BudgetAlertCard } from "@/components/reports/BudgetAlertCard";
import { TechnicianDashboard } from "@/pages/reports/TechnicianDashboard";

const STATUS_LABELS = {
  recibido: "Recibidos",
  en_diagnostico: "En diagnóstico",
  pendiente_autorizacion: "Pend. autorización",
  autorizado: "Autorizados",
  en_proceso: "En proceso",
  pausa: "En pausa",
  listo_para_entrega: "Listo entrega",
  entregado: "Entregados",
  cancelado: "Cancelados",
};

export default function Dashboard() {
  const { session, hasPermission } = useAuth();
  const { activeTenant, isSuperAdmin, activeTenantId } = useTenant();
  const canReports = hasPermission("reports:view");
  const isTechnicianOnly = !canReports;

  if (isTechnicianOnly) {
    return <TechnicianDashboard />;
  }

  return <ManagementDashboard session={session} activeTenant={activeTenant} isSuperAdmin={isSuperAdmin} activeTenantId={activeTenantId} hasPermission={hasPermission} />;
}

function ManagementDashboard({ session, activeTenant, isSuperAdmin, activeTenantId, hasPermission }) {
  const navigate = useNavigate();
  const [range, setRange] = useState(() => ({ preset: "month", ...presetRange("month") }));
  const [branchId, setBranchId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = {};
    if (range.date_from) p.date_from = range.date_from;
    if (range.date_to) p.date_to = range.date_to;
    if (branchId) p.branch_id = branchId;
    if (isSuperAdmin && activeTenantId) p.tenant_id = activeTenantId;
    return p;
  }, [range, branchId, isSuperAdmin, activeTenantId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get("/reports/dashboard", { params });
        if (!cancelled) setData(d);
      } catch (_) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [params]);

  const kpis = data?.kpis || {};
  const daily = data?.daily || [];
  const byStatus = data?.orders_by_status || {};
  const topCustomers = data?.top_customers || [];
  const statusRows = Object.entries(STATUS_LABELS).map(([k, label]) => ({
    key: k,
    label,
    count: byStatus[k] || 0,
  }));

  return (
    <div className="space-y-6" data-testid="page-dashboard">
      <PageHeader
        title={session?.user?.full_name ? `Hola, ${session.user.full_name.split(" ")[0]}` : "Panel de control"}
        description={
          activeTenant
            ? `${activeTenant.commercial_name} · Panel financiero y operativo`
            : "Vista global VRIntegrales"
        }
        actions={
          <Badge variant="outline" className="border-tenant text-tenant" data-testid="dashboard-role-badge">
            {session?.user?.role_name || "usuario"}
          </Badge>
        }
      />

      {/* Filters */}
      <Card className="border-border">
        <CardContent className="p-4 flex flex-col lg:flex-row gap-4 lg:items-end lg:justify-between">
          <DateRangeFilter value={range} onChange={setRange} />
          <BranchFilter value={branchId} onChange={setBranchId} />
        </CardContent>
      </Card>

      {/* Reorder alert (Fase 4.1) */}
      {hasPermission("analytics:view") && <ReorderAlert branchId={branchId} />}

      {/* Alertas operativas (Fase 8.1) */}
      <AlertsWidget tenantParam={isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {}} />

      {/* Budget alert (Fase 4.4) — silent when nothing to alert */}
      {hasPermission("budgets:view") && <BudgetAlertCard branchId={branchId} />}

      {/* Primary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-grid">
        <KpiCard
          label="Ventas hoy"
          value={formatMxn(kpis.sales_today)}
          icon={Receipt}
          testId="kpi-sales-today"
          loading={loading}
        />
        <KpiCard
          label="Ventas semana"
          value={formatMxn(kpis.sales_week)}
          icon={Receipt}
          testId="kpi-sales-week"
          loading={loading}
        />
        <KpiCard
          label="Ventas mes"
          value={formatMxn(kpis.sales_month)}
          icon={Receipt}
          testId="kpi-sales-month"
          loading={loading}
        />
        <KpiCard
          label="Ticket promedio"
          value={formatMxn(kpis.avg_ticket)}
          hint={`${formatInt(kpis.sales_period_count)} ventas en el período`}
          icon={Wallet}
          testId="kpi-avg-ticket"
          loading={loading}
        />
        <KpiCard
          label="Anticipos cobrados"
          value={formatMxn(kpis.payments_period_total)}
          icon={Wallet}
          testId="kpi-payments"
          loading={loading}
        />
        <KpiCard
          label="Saldo pendiente"
          value={formatMxn(kpis.pending_balance_total)}
          icon={AlertTriangle}
          testId="kpi-balance"
          loading={loading}
        />
      </div>

      {/* Fase 4.2 — Gastos + Balance neto */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-grid-financial">
        <KpiCard
          label="Gastos del período"
          value={formatMxn(kpis.expenses_period_total)}
          hint={`Hoy ${formatMxn(kpis.expenses_today)} · Sem ${formatMxn(kpis.expenses_week)}`}
          icon={TrendingDown}
          testId="kpi-expenses-period"
          loading={loading}
          onClick={() => navigate("/gastos")}
        />
        <KpiCard
          label="Gastos hoy"
          value={formatMxn(kpis.expenses_today)}
          icon={TrendingDown}
          testId="kpi-expenses-today"
          loading={loading}
        />
        <KpiCard
          label="Resultado operativo"
          value={formatMxn(kpis.net_operating_period)}
          hint="Ventas cerradas − gastos aprobados"
          icon={Scale}
          testId="kpi-net-operating"
          loading={loading}
          tone={kpis.net_operating_period < 0 ? "border-red-200 bg-red-50/30" : ""}
        />
        <KpiCard
          label="Flujo neto de caja"
          value={formatMxn(kpis.net_cash_period)}
          hint="Pagos recibidos − gastos aprobados"
          icon={Scale}
          testId="kpi-net-cash"
          loading={loading}
          tone={kpis.net_cash_period < 0 ? "border-red-200 bg-red-50/30" : ""}
        />
      </div>

      {/* Ops KPIs + inventory */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiCard
          label="Órdenes abiertas"
          value={formatInt(kpis.orders_open)}
          icon={ClipboardList}
          testId="kpi-orders-open"
          onClick={() => navigate("/ordenes-de-servicio")}
        />
        <KpiCard
          label="En proceso"
          value={formatInt(kpis.orders_in_process)}
          icon={ClipboardList}
          testId="kpi-orders-in-process"
        />
        <KpiCard
          label="Listas para entrega"
          value={formatInt(kpis.orders_ready)}
          icon={ClipboardList}
          testId="kpi-orders-ready"
        />
        <KpiCard
          label="Entregadas"
          value={formatInt(kpis.orders_delivered)}
          icon={ClipboardList}
          testId="kpi-orders-delivered"
        />
        <KpiCard
          label="Refacciones bajo mínimo"
          value={formatInt(kpis.parts_low_stock)}
          icon={Package}
          testId="kpi-low-stock"
          onClick={() => navigate("/inventario/alertas")}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border" data-testid="chart-daily-sales">
          <CardHeader className="border-b border-border">
            <CardTitle className="tracking-tight">Ventas por día</CardTitle>
            <CardDescription>
              Total de ventas cerradas y anticipos recibidos por día (MX).
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {loading ? (
              <Skeleton className="h-64" />
            ) : (
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <LineChart data={daily} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                    <CartesianGrid stroke="#eef2f7" strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => v.slice(5)}
                    />
                    <YAxis tick={{ fontSize: 11 }} width={70}
                      tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
                    <Tooltip
                      formatter={(v, name) => [formatMxn(v), name === "sales" ? "Ventas" : "Anticipos"]}
                      labelFormatter={(l) => `Día: ${l}`}
                    />
                    <Line type="monotone" dataKey="sales" stroke="var(--tenant-primary)" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="payments" stroke="#94a3b8" strokeWidth={2} dot={false} strokeDasharray="4 4" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border" data-testid="chart-status">
          <CardHeader className="border-b border-border">
            <CardTitle className="tracking-tight">Órdenes por estatus</CardTitle>
            <CardDescription>Foto operativa actual del taller.</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {loading ? (
              <Skeleton className="h-64" />
            ) : (
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={statusRows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                    <CartesianGrid stroke="#eef2f7" strokeDasharray="3 3" />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10 }}
                      angle={-25}
                      textAnchor="end"
                      height={70}
                      interval={0}
                    />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={30} />
                    <Tooltip />
                    <Bar dataKey="count" fill="var(--tenant-primary)" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom: top customers + status grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border" data-testid="card-status-grid">
          <CardHeader className="border-b border-border flex flex-row items-start justify-between">
            <div>
              <CardTitle className="tracking-tight">Estatus operativo</CardTitle>
              <CardDescription>Conteo de folios por estatus.</CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/ordenes-de-servicio")}
              data-testid="dashboard-goto-service-orders"
            >
              Ver órdenes <ArrowUpRight className="h-4 w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {statusRows.map((s) => (
                <div
                  key={s.key}
                  className="border rounded-md p-3 flex flex-col justify-between h-20 bg-slate-50/40 border-slate-200"
                  data-testid={`status-cell-${s.key}`}
                >
                  <div className="text-[11px] uppercase tracking-wider text-slate-600">
                    {s.label}
                  </div>
                  <div className="text-xl font-semibold" style={{ fontFamily: "Chivo, sans-serif" }}>
                    {s.count}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-border" data-testid="card-top-customers">
          <CardHeader className="border-b border-border">
            <CardTitle className="tracking-tight">Top clientes del período</CardTitle>
            <CardDescription>Por ingresos (ventas cerradas).</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4 space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-10" />
                ))}
              </div>
            ) : topCustomers.length ? (
              <ul className="divide-y divide-border">
                {topCustomers.map((c) => (
                  <li key={c.customer_id} className="p-4 flex items-center gap-3" data-testid={`top-customer-${c.customer_id}`}>
                    <div className="h-8 w-8 rounded bg-slate-100 text-slate-600 flex items-center justify-center text-xs font-semibold">
                      {(c.legal_name || "?").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{c.legal_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {c.orders} {c.orders === 1 ? "orden" : "órdenes"}
                      </div>
                    </div>
                    <div className="text-sm font-semibold" style={{ fontFamily: "Chivo, sans-serif" }}>
                      {formatMxn(c.revenue)}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-6 text-sm text-muted-foreground">
                Aún no hay ventas cerradas en el período.
              </div>
            )}
            <div className="p-3 border-t border-border">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-between"
                onClick={() => navigate("/reportes/top-clientes")}
                data-testid="dashboard-goto-top-customers"
              >
                Ver reporte completo <ArrowUpRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
